import { FieldPath, UseFormSetError } from 'react-hook-form';

import {
  MAX_AUTO_VEHICLES,
  MIN_AUTO_VEHICLE_YEAR,
  canonicalAutoVehicleType,
  isAutoVehicleYear,
  maxAutoVehicleYear,
} from 'lib/autoTransport/autoVehicleTypes';
import { Undef } from 'types/UtilityTypes';

import {
  isSuperDispatchVehicleCatalogLoaded,
  resolveCatalogMake,
  resolveCatalogModel,
} from '../FTL/superDispatchVehicleCatalog';
import { formatFieldLabel } from '../LTL/accessorialUtils';
import { LTL_FREIGHT_CLASSES, QuickQuoteInputs, QuoteFormStop } from '../types';
import { parseLocation } from './location';
import { hasValidStructuredLocation } from './normalizeStopsForQuickQuoteSubmission';
import { isStopLocationValidForRating } from './parseStopAddressFromLocation';
import { isValidDateTimeWindow } from './resolveTaiLtlPickupWindow';

export type ValidateLTLFormOptions = {
  acceptFullStreetLocations?: boolean;
  validateFreightClassAgainstDropdown?: boolean;
  // Tai LTL: require pickup ready/close and enforce ready/estimated <= close windows.
  validateTaiPickupDeliveryWindows?: boolean;
};

const DEFAULT_STOP_LOCATION_ERROR_MESSAGE =
  'Please enter a valid location (ZIP, City, ST, Canadian postal)';

const TAI_STOP_LOCATION_ERROR_MESSAGE =
  'Enter a ZIP/postal code, City + State, or a full address.';

const LTL_FREIGHT_CLASS_SET = new Set<string>(LTL_FREIGHT_CLASSES);

function isTurvoLTLFlow(formValues: QuickQuoteInputs): boolean {
  return (
    formValues.ltl?.serviceType !== undefined ||
    formValues.ltl?.paymentTerm !== undefined
  );
}

function validateStopsWithPredicate(
  formValues: QuickQuoteInputs,
  setError: UseFormSetError<QuickQuoteInputs>,
  isValidStop: (stop: QuoteFormStop, formValues: QuickQuoteInputs) => boolean,
  invalidLocationMessage = DEFAULT_STOP_LOCATION_ERROR_MESSAGE
): Array<[FieldPath<QuickQuoteInputs>, string]> {
  const validationErrors: Array<[FieldPath<QuickQuoteInputs>, string]> = [];
  formValues.stops.forEach((stop, index) => {
    if (!isValidStop(stop, formValues)) {
      validationErrors.push([
        `stops.${index}.location`,
        invalidLocationMessage,
      ]);
    }
  });

  if (validationErrors.length) {
    validationErrors.forEach(([field, message]) =>
      setError(field as FieldPath<QuickQuoteInputs>, { message })
    );
  }
  return validationErrors;
}

/**
 * Accepts quote location text formats (via parseLocation), including:
 * - US ZIP / ZIP+4
 * - City, ST / City, ST ZIP
 * - Canadian postal (standalone, city comma province postal, city then postal).
 * Turvo keeps non-empty behavior only.
 */
export function validateStopsAllowQuoteLocationFormats(
  formValues: QuickQuoteInputs,
  setError: UseFormSetError<QuickQuoteInputs>
): Array<[FieldPath<QuickQuoteInputs>, string]> {
  const isTurvoFlow = isTurvoLTLFlow(formValues);
  return validateStopsWithPredicate(formValues, setError, (stop) => {
    const location = (stop.location ?? '').trim();
    if (isTurvoFlow) {
      return Boolean(location);
    }

    return hasValidStructuredLocation(stop) || Boolean(parseLocation(location));
  });
}

/**
 * Validates LTL-specific form fields including items and accessorials.
 * Stop locations use the same parseLocation rules as FTL (non-Turvo), so Canadian
 * and US combined formats are accepted when parseable.
 */
export function validateLTLForm(
  formValues: QuickQuoteInputs,
  setError: UseFormSetError<QuickQuoteInputs>,
  options?: ValidateLTLFormOptions
): Array<[FieldPath<QuickQuoteInputs>, string]> {
  const validationErrors: Array<[FieldPath<QuickQuoteInputs>, string]> = [];
  const isTurvoFlow = isTurvoLTLFlow(formValues);
  const acceptFullStreetLocations = options?.acceptFullStreetLocations ?? false;
  const stopLocationErrorMessage = acceptFullStreetLocations
    ? TAI_STOP_LOCATION_ERROR_MESSAGE
    : DEFAULT_STOP_LOCATION_ERROR_MESSAGE;

  const stopErrors = validateStopsWithPredicate(
    formValues,
    setError,
    (stop) => {
      const location = (stop.location ?? '').trim();
      if (isTurvoFlow) {
        return Boolean(location);
      }

      if (parseLocation(location)) {
        return true;
      }

      if (hasValidStructuredLocation(stop)) {
        return true;
      }

      if (acceptFullStreetLocations) {
        return isStopLocationValidForRating(location);
      }

      return false;
    },
    stopLocationErrorMessage
  );
  validationErrors.push(...stopErrors);

  const items = formValues.ltl?.items;
  if (!items || items.length === 0) {
    validationErrors.push([
      'ltl.items' as FieldPath<QuickQuoteInputs>,
      'At least one item is required',
    ]);
  } else {
    items.forEach((item, index) => {
      const hasDescription = item.description && item.description.trim() !== '';
      const freightClass = item.freightClass?.trim() ?? '';
      const hasFreightClass = freightClass !== '';
      const hasValidFreightClass =
        !options?.validateFreightClassAgainstDropdown ||
        LTL_FREIGHT_CLASS_SET.has(freightClass);
      // Turvo uses handlingUnits as qty; other integrations may use pieces.
      const hasPieces = typeof item.pieces === 'number' && item.pieces > 0;
      const hasHandlingUnits =
        typeof item.handlingUnits === 'number' && item.handlingUnits > 0;
      const hasQuantity = hasPieces || hasHandlingUnits;
      const hasWeight = typeof item.weight === 'number' && item.weight > 0;

      if (!hasDescription) {
        validationErrors.push([
          `ltl.items.${index}.description` as FieldPath<QuickQuoteInputs>,
          'Description is required',
        ]);
      }
      if (!hasQuantity) {
        validationErrors.push([
          (isTurvoFlow
            ? `ltl.items.${index}.handlingUnits`
            : `ltl.items.${index}.pieces`) as FieldPath<QuickQuoteInputs>,
          isTurvoFlow
            ? 'Quantity must be greater than 0'
            : 'Pieces must be greater than 0',
        ]);
      }
      if (!hasFreightClass) {
        validationErrors.push([
          `ltl.items.${index}.freightClass` as FieldPath<QuickQuoteInputs>,
          'Freight class is required',
        ]);
      } else if (!hasValidFreightClass) {
        validationErrors.push([
          `ltl.items.${index}.freightClass` as FieldPath<QuickQuoteInputs>,
          'Select a valid freight class.',
        ]);
      }
      if (!hasWeight) {
        validationErrors.push([
          `ltl.items.${index}.weight` as FieldPath<QuickQuoteInputs>,
          'Weight must be greater than 0',
        ]);
      }
    });
  }

  const selectedAccessorials = formValues.ltl?.selectedAccessorials;
  if (selectedAccessorials && selectedAccessorials.length > 0) {
    selectedAccessorials.forEach((accessorial, index) => {
      if (!accessorial?.code || !accessorial?.actionValues) {
        return;
      }

      Object.entries(accessorial.actionValues).forEach(([fieldName, value]) => {
        const isEmpty =
          value === undefined ||
          value === null ||
          (typeof value === 'string' && value.trim() === '');

        if (isEmpty) {
          validationErrors.push([
            `ltl.selectedAccessorials.${index}.actionValues.${fieldName}` as FieldPath<QuickQuoteInputs>,
            `${formatFieldLabel(fieldName)} is required`,
          ]);
        }
      });
    });
  }

  if (options?.validateTaiPickupDeliveryWindows) {
    const pickupReady = formValues.pickupDate;
    const pickupClose = formValues.pickupCloseDate;
    const hasValidPickupReady =
      pickupReady instanceof Date && !Number.isNaN(pickupReady.getTime());
    const hasValidPickupClose =
      pickupClose instanceof Date && !Number.isNaN(pickupClose.getTime());

    if (!hasValidPickupReady) {
      validationErrors.push(['pickupDate', 'Ready is required']);
    }

    if (!hasValidPickupClose) {
      validationErrors.push(['pickupCloseDate', 'Close is required']);
    }

    if (
      hasValidPickupReady &&
      hasValidPickupClose &&
      !isValidDateTimeWindow(pickupReady, pickupClose)
    ) {
      validationErrors.push([
        'pickupCloseDate',
        'Close must be on or after Ready',
      ]);
    }

    const deliveryEstimated = formValues.deliveryDate;
    const deliveryClose = formValues.deliveryCloseDate;
    const hasValidDeliveryEstimated =
      deliveryEstimated instanceof Date &&
      !Number.isNaN(deliveryEstimated.getTime());
    const hasValidDeliveryClose =
      deliveryClose instanceof Date && !Number.isNaN(deliveryClose.getTime());

    if (
      hasValidDeliveryEstimated &&
      hasValidDeliveryClose &&
      !isValidDateTimeWindow(deliveryEstimated, deliveryClose)
    ) {
      validationErrors.push([
        'deliveryCloseDate',
        'Close must be on or after Estimated',
      ]);
    }
  }

  if (validationErrors.length > 0) {
    validationErrors.forEach(([field, message]) => {
      setError(field, { message });
    });
  }

  return validationErrors;
}

// Used by field validation and submit validation so both show the same error.
export function autoTransportYearError(year: string): Undef<string> {
  if (!year.trim()) {
    return 'Required';
  }

  if (!isAutoVehicleYear(year)) {
    return `Enter a year from ${MIN_AUTO_VEHICLE_YEAR} to ${maxAutoVehicleYear()}`;
  }

  return undefined;
}

export function autoTransportMakeError(make: string): Undef<string> {
  if (!make.trim()) {
    return 'Required';
  }

  // Catalog loads on submit; don't block handleSubmit so submitQuote can retry.
  if (!isSuperDispatchVehicleCatalogLoaded()) {
    return undefined;
  }

  if (!resolveCatalogMake(make)) {
    return 'Select a make';
  }

  return undefined;
}

export function autoTransportModelError(
  make: string,
  model: string
): Undef<string> {
  if (!model.trim()) {
    return 'Required';
  }

  if (!isSuperDispatchVehicleCatalogLoaded()) {
    return undefined;
  }

  const resolvedMake = resolveCatalogMake(make);
  if (!resolvedMake || !resolveCatalogModel(resolvedMake, model)) {
    return 'Select a model';
  }

  return undefined;
}

export function autoTransportTypeError(type: string): Undef<string> {
  if (!canonicalAutoVehicleType(type)) {
    return 'Select a type';
  }

  return undefined;
}

export function validateAutoTransport(
  formValues: QuickQuoteInputs,
  setError: UseFormSetError<QuickQuoteInputs>
): Array<[FieldPath<QuickQuoteInputs>, string]> {
  const validationErrors: Array<[FieldPath<QuickQuoteInputs>, string]> = [];
  const details = formValues.autoTransport;
  const vehicles = details?.vehicles ?? [];

  if (!details?.trailerType) {
    validationErrors.push(['autoTransport.trailerType', 'Required']);
  }

  if (vehicles.length === 0) {
    validationErrors.push([
      'autoTransport.vehicles',
      'Add at least one vehicle',
    ]);
  }

  if (vehicles.length > MAX_AUTO_VEHICLES) {
    validationErrors.push([
      'autoTransport.vehicles',
      `Maximum ${MAX_AUTO_VEHICLES} vehicles`,
    ]);
  }

  vehicles.forEach((vehicle, index) => {
    const vehiclePath = `autoTransport.vehicles.${index}` as const;
    const vehicleErrors: Array<[FieldPath<QuickQuoteInputs>, Undef<string>]> = [
      [`${vehiclePath}.year`, autoTransportYearError(vehicle.year)],
      [`${vehiclePath}.make`, autoTransportMakeError(vehicle.make)],
      [
        `${vehiclePath}.model`,
        autoTransportModelError(vehicle.make, vehicle.model),
      ],
      [`${vehiclePath}.type`, autoTransportTypeError(vehicle.type)],
    ];

    vehicleErrors.forEach(([field, message]) => {
      if (message) {
        validationErrors.push([field, message]);
      }
    });
  });

  validationErrors.forEach(([field, message]) => {
    setError(field, { message });
  });

  return validationErrors;
}
